import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
import { api } from './lib/api';
import Navbar from './components/Navbar';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import { ToastProvider, useToast } from './components/Toast';
import SurpriseMe from './components/SurpriseMe';
import HomePage from './pages/HomePage';
import StoryPage from './pages/StoryPage';
import ReaderPage from './pages/ReaderPage';
import DiscoverPage from './pages/DiscoverPage';
import DashboardPage from './pages/DashboardPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AuthPage from './pages/AuthPage';
import ProfilePage from './pages/ProfilePage';
import ContestPage from './pages/ContestPage';
import LibraryPage from './pages/LibraryPage';
import AdminPage from './pages/AdminPage';
import LegalPage from './pages/LegalPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import WelcomePage from './pages/WelcomePage';
import PenNamesPage from './pages/PenNamesPage';
import InkPage from './pages/InkPage';
import ForumsPage from './pages/ForumsPage';
import MessagesPage from './pages/MessagesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import Footer from './components/Footer';

type LegalSection = 'terms' | 'privacy' | 'content-guidelines' | 'community-guidelines';

type PageState =
  | { page: 'home' }
  | { page: 'story'; id: number; slug?: string }
  | { page: 'reader'; chapterId: number; storySlug?: string; chapterNum?: number; resumeScroll?: number }
  | { page: 'discover'; genre?: string; tag?: string }
  | { page: 'dashboard' }
  | { page: 'admin' }
  | { page: 'library' }
  | { page: 'leaderboard' }
  | { page: 'contest' }
  | { page: 'login' }
  | { page: 'register' }
  | { page: 'profile'; username: string }
  | { page: 'verify-email'; token: string }
  | { page: 'welcome' }
  | { page: 'terms' }
  | { page: 'privacy' }
  | { page: 'content-guidelines' }
  | { page: 'community-guidelines' }
  | { page: 'pen-names' }
  | { page: 'pen-profile'; penName: string }
  | { page: 'ink' }
  | { page: 'forums' }
  | { page: 'forum-category'; slug: string }
  | { page: 'forum-thread'; id: number }
  | { page: 'messages'; conversationId?: number; username?: string }
  | { page: 'forgot-password' }
  | { page: 'reset-password'; token: string }
  | { page: 'account-settings' }
  | { page: 'blog' }
  | { page: 'blog-post'; slug: string };

const PROTECTED_PAGES = ['dashboard', 'library', 'contest', 'pen-names'];

function pageStateToPath(state: PageState): string {
  switch (state.page) {
    case 'home': return '/';
    case 'story': return state.slug ? `/story/${state.slug}` : `/story/${state.id}`;
    case 'reader': {
      if (state.storySlug && state.chapterNum) {
        return `/read/${state.storySlug}/chapter-${state.chapterNum}`;
      }
      return `/read/${state.chapterId}`;
    }
    case 'discover': {
      const params = new URLSearchParams();
      if (state.genre) params.set('genre', state.genre);
      if (state.tag) params.set('tag', state.tag);
      const qs = params.toString();
      return '/discover' + (qs ? '?' + qs : '');
    }
    case 'dashboard': return '/dashboard';
    case 'admin': return '/admin';
    case 'library': return '/library';
    case 'leaderboard': return '/leaderboard';
    case 'contest': return '/contest';
    case 'login': return '/login';
    case 'register': return '/register';
    case 'profile': return `/profile/${state.username}`;
    case 'verify-email': return `/verify-email?token=${state.token}`;
    case 'welcome': return '/welcome';
    case 'terms': return '/terms';
    case 'privacy': return '/privacy';
    case 'content-guidelines': return '/content-guidelines';
    case 'community-guidelines': return '/community-guidelines';
    case 'pen-names': return '/pen-names';
    case 'pen-profile': return `/pen-profile/${state.penName}`;
    case 'ink': return '/ink';
    case 'forums': return '/forums';
    case 'forum-category': return `/forums/${state.slug}`;
    case 'forum-thread': return `/forums/thread/${state.id}`;
    case 'messages': return '/messages';
    case 'account-settings': return '/account-settings';
    case 'forgot-password': return '/forgot-password';
    case 'reset-password': return `/reset-password?token=${state.token}`;
    case 'blog': return '/blog';
    case 'blog-post': return `/blog/${state.slug}`;
    default: return '/';
  }
}

function pathToPageState(pathname: string, search: string): PageState {
  const params = new URLSearchParams(search);

  const verified = params.get('verified');
  if (verified === 'success' || verified === 'already') {
    return { page: 'welcome' };
  }

  const verifyToken = params.get('verify');
  if (verifyToken) {
    return { page: 'verify-email', token: verifyToken };
  }

  if (pathname === '/' || pathname === '') return { page: 'home' };
  if (pathname === '/discover') return { page: 'discover', genre: params.get('genre') || undefined, tag: params.get('tag') || undefined };
  if (pathname === '/dashboard') return { page: 'dashboard' };
  if (pathname === '/admin') return { page: 'admin' };
  if (pathname === '/library') return { page: 'library' };
  if (pathname === '/leaderboard') return { page: 'leaderboard' };
  if (pathname === '/contest' || pathname === '/contests') return { page: 'contest' };
  if (pathname === '/login') return { page: 'login' };
  if (pathname === '/register') return { page: 'register' };
  if (pathname === '/welcome') return { page: 'welcome' };
  if (pathname === '/terms') return { page: 'terms' };
  if (pathname === '/privacy') return { page: 'privacy' };
  if (pathname === '/content-guidelines') return { page: 'content-guidelines' };
  if (pathname === '/community-guidelines') return { page: 'community-guidelines' };
  if (pathname === '/pen-names') return { page: 'pen-names' };
  if (pathname === '/ink') return { page: 'ink' };
  if (pathname === '/messages') return { page: 'messages' };
  if (pathname === '/account-settings') return { page: 'account-settings' };
  if (pathname === '/blog') return { page: 'blog' };
  if (pathname === '/forgot-password') return { page: 'forgot-password' };
  if (pathname === '/reset-password') {
    const resetToken = params.get('token');
    if (resetToken) return { page: 'reset-password', token: resetToken };
    return { page: 'login' };
  }
  if (pathname === '/forums') return { page: 'forums' };

  const blogPostMatch = pathname.match(/^\/blog\/(.+)$/);
  if (blogPostMatch) {
    return { page: 'blog-post', slug: decodeURIComponent(blogPostMatch[1]) };
  }

  const forumThreadMatch = pathname.match(/^\/forums\/thread\/(\d+)$/);
  if (forumThreadMatch) {
    return { page: 'forum-thread', id: Number(forumThreadMatch[1]) };
  }

  const forumCategoryMatch = pathname.match(/^\/forums\/(.+)$/);
  if (forumCategoryMatch) {
    return { page: 'forum-category', slug: decodeURIComponent(forumCategoryMatch[1]) };
  }

  if (pathname === '/verify-email') {
    const token = params.get('token');
    if (token) return { page: 'verify-email', token };
    return { page: 'home' };
  }

  const storyMatch = pathname.match(/^\/story\/(.+)$/);
  if (storyMatch) {
    const param = storyMatch[1];
    const asNum = Number(param);
    if (!isNaN(asNum) && String(asNum) === param) {
      return { page: 'story', id: asNum };
    }
    return { page: 'story', id: 0, slug: param };
  }

  const readSlugMatch = pathname.match(/^\/read\/(.+)\/chapter-(\d+)$/);
  if (readSlugMatch) {
    return { page: 'reader', chapterId: 0, storySlug: readSlugMatch[1], chapterNum: Number(readSlugMatch[2]) };
  }

  const readIdMatch = pathname.match(/^\/read\/(\d+)$/);
  if (readIdMatch) {
    return { page: 'reader', chapterId: Number(readIdMatch[1]) };
  }

  const profileMatch = pathname.match(/^\/profile\/(.+)$/);
  if (profileMatch) {
    return { page: 'profile', username: decodeURIComponent(profileMatch[1]) };
  }

  const penProfileMatch = pathname.match(/^\/pen-profile\/(.+)$/);
  if (penProfileMatch) {
    return { page: 'pen-profile', penName: decodeURIComponent(penProfileMatch[1]) };
  }

  return { page: 'home' };
}

function getPageTitle(state: PageState): string {
  const base = 'ScribeHaven';
  switch (state.page) {
    case 'home': return `${base} \u2014 Free Serial Platform for Romance & Romantasy | Rising Stars & Trope Tags`;
    case 'discover': return `Discover Stories \u2014 Browse by Genre, Tags & Popularity | ${base}`;
    case 'dashboard': return `Author Dashboard \u2014 Manage Your Stories | ${base}`;
    case 'admin': return `Admin Panel \u2014 ${base}`;
    case 'library': return `My Library \u2014 Your Personal Reading List | ${base}`;
    case 'leaderboard': return `Top Stories & Rising Authors \u2014 Leaderboard | ${base}`;
    case 'contest': return `Writing Contests \u2014 Compete, Win & Get Discovered | ${base}`;
    case 'login': return `Sign In to ${base} \u2014 Access Your Stories & Library`;
    case 'register': return `Create Your Free Account \u2014 Start Reading & Writing | ${base}`;
    case 'welcome': return `Welcome to ${base} \u2014 Your Journey Starts Here`;
    case 'terms': return `Terms of Service | ${base}`;
    case 'privacy': return `Privacy Policy | ${base}`;
    case 'content-guidelines': return `Content Guidelines \u2014 What You Can Publish | ${base}`;
    case 'community-guidelines': return `Community Guidelines \u2014 Be Respectful & Creative | ${base}`;
    case 'pen-names': return `Pen Names \u2014 Write Under Any Identity | ${base}`;
    case 'ink': return `Ink \u2014 Support Your Favorite Authors | ${base}`;
    case 'forums': return `Community Forums \u2014 Discuss, Share & Connect | ${base}`;
    case 'forum-category': return `Forums \u2014 ${base}`;
    case 'forum-thread': return `Forum Thread \u2014 ${base}`;
    case 'messages': return `Messages \u2014 Direct Conversations | ${base}`;
    case 'account-settings': return `Account Settings \u2014 Manage Your Profile | ${base}`;
    case 'forgot-password': return `Forgot Password \u2014 Reset Your Account | ${base}`;
    case 'reset-password': return `Reset Password | ${base}`;
    case 'profile': return `${state.username}'s Profile \u2014 Stories, Reviews & More | ${base}`;
    case 'pen-profile': return `${state.penName} \u2014 Author Profile | ${base}`;
    case 'story': return state.slug ? `${state.slug.replace(/-/g, ' ')} \u2014 ${base}` : base;
    case 'reader': return base;
    case 'verify-email': return `Verify Email \u2014 ${base}`;
    case 'blog': return `Blog \u2014 Writing Tips, Platform Updates & Author Resources | ${base}`;
    case 'blog-post': return `${base} Blog`;
    default: return base;
  }
}

export default function App() {
  const { user, loading, login, register, logout, setUser } = useAuth();
  const toast = useToast();
  const [pageState, setPageState] = useState<PageState>(() =>
    pathToPageState(window.location.pathname, window.location.search)
  );

  useEffect(() => {
    const verified = new URLSearchParams(window.location.search).get('verified');
    if (verified === 'success' || verified === 'already') {
      api.auth.me().then(setUser).catch(() => {});
      window.history.replaceState({}, '', '/welcome');
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const newState = pathToPageState(window.location.pathname, window.location.search);
      setPageState(newState);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    document.title = getPageTitle(pageState);
  }, [pageState]);

  const updateTitle = useCallback((title: string) => {
    document.title = title;
  }, []);

  const onNavigate = useCallback((page: string, data?: any) => {
    window.scrollTo(0, 0);
    let newState: PageState;
    switch (page) {
      case 'home': newState = { page: 'home' }; break;
      case 'story': newState = { page: 'story', id: data.id, slug: data.slug }; break;
      case 'reader': newState = { page: 'reader', chapterId: data.chapterId, storySlug: data.storySlug, chapterNum: data.chapterNum, resumeScroll: data.resumeScroll }; break;
      case 'discover': newState = { page: 'discover', genre: data?.genre, tag: data?.tag }; break;
      case 'dashboard': newState = { page: 'dashboard' }; break;
      case 'admin': newState = { page: 'admin' }; break;
      case 'library': newState = { page: 'library' }; break;
      case 'leaderboard': newState = { page: 'leaderboard' }; break;
      case 'contest': newState = { page: 'contest' }; break;
      case 'login': newState = { page: 'login' }; break;
      case 'register': newState = { page: 'register' }; break;
      case 'profile': newState = { page: 'profile', username: data.username }; break;
      case 'verify-email': newState = { page: 'verify-email', token: data.token }; break;
      case 'welcome': newState = { page: 'welcome' }; break;
      case 'terms': newState = { page: 'terms' }; break;
      case 'privacy': newState = { page: 'privacy' }; break;
      case 'content-guidelines': newState = { page: 'content-guidelines' }; break;
      case 'community-guidelines': newState = { page: 'community-guidelines' }; break;
      case 'pen-names': newState = { page: 'pen-names' }; break;
      case 'pen-profile': newState = { page: 'pen-profile', penName: data.penName }; break;
      case 'ink': newState = { page: 'ink' }; break;
      case 'forums': newState = { page: 'forums' }; break;
      case 'forum-category': newState = { page: 'forum-category', slug: data.slug }; break;
      case 'forum-thread': newState = { page: 'forum-thread', id: data.id }; break;
      case 'messages': newState = { page: 'messages', conversationId: data?.conversationId, username: data?.username }; break;
      case 'account-settings': newState = { page: 'account-settings' }; break;
      case 'forgot-password': newState = { page: 'forgot-password' }; break;
      case 'reset-password': newState = { page: 'reset-password', token: data?.token }; break;
      case 'blog': newState = { page: 'blog' }; break;
      case 'blog-post': newState = { page: 'blog-post', slug: data?.slug }; break;
      default: return;
    }
    setPageState(newState);
    const newPath = pageStateToPath(newState);
    window.history.pushState(null, '', newPath);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-center">
          <span className="text-4xl block mb-4">✦</span>
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  const showNavbar = pageState.page !== 'reader';
  const isProtectedPage = PROTECTED_PAGES.includes(pageState.page);
  const needsVerification = user && !user.emailVerified && isProtectedPage;

  return (
    <ToastProvider>
    <div className="min-h-screen bg-gray-950">
      {showNavbar && (
        <>
          <Navbar
            user={user}
            onNavigate={onNavigate}
            onLogout={logout}
            currentPage={pageState.page}
          />
          <EmailVerificationBanner user={user} />
        </>
      )}

      {needsVerification ? (
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/20">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-3">Verify Your Email First</h2>
          <p className="text-gray-400 mb-6">
            You need to verify your email address before you can access this feature. Check your inbox for a verification email from ScribeHaven.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                api.auth.resendVerification()
                  .then(() => toast.success('Verification email sent! Check your inbox.'))
                  .catch((err: any) => toast.error(err.message || 'Failed to send'));
              }}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-gray-950 rounded-xl font-semibold transition-colors"
            >
              Resend Verification Email
            </button>
            <button
              onClick={() => onNavigate('home')}
              className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      ) : (
        <>
          {pageState.page === 'home' && <HomePage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'story' && <StoryPage storyId={pageState.id} slug={pageState.slug} onNavigate={onNavigate} user={user} onTitleReady={updateTitle} />}
          {pageState.page === 'reader' && <ReaderPage chapterId={pageState.chapterId} storySlug={pageState.storySlug} chapterNum={pageState.chapterNum} resumeScroll={pageState.resumeScroll} onNavigate={onNavigate} user={user} onTitleReady={updateTitle} />}
          {pageState.page === 'discover' && <DiscoverPage onNavigate={onNavigate} initialGenre={pageState.genre} initialTag={pageState.tag} user={user} />}
          {pageState.page === 'dashboard' && <DashboardPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'admin' && <AdminPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'library' && <LibraryPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'leaderboard' && <LeaderboardPage onNavigate={onNavigate} />}
          {pageState.page === 'contest' && <ContestPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'login' && <AuthPage mode="login" onLogin={login} onRegister={register} onNavigate={onNavigate} />}
          {pageState.page === 'register' && <AuthPage mode="register" onLogin={login} onRegister={register} onNavigate={onNavigate} />}
          {pageState.page === 'profile' && <ProfilePage username={pageState.username} onNavigate={onNavigate} user={user} />}
          {pageState.page === 'verify-email' && (
            <VerifyEmailPage
              token={pageState.token}
              onNavigate={onNavigate}
              onVerified={() => {
                api.auth.me().then(setUser).catch(() => {});
              }}
            />
          )}
          {pageState.page === 'welcome' && <WelcomePage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'pen-names' && <PenNamesPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'pen-profile' && <ProfilePage username={pageState.penName} onNavigate={onNavigate} isPenProfile />}
          {pageState.page === 'ink' && <InkPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'forums' && <ForumsPage onNavigate={onNavigate} user={user} />}
          {pageState.page === 'forum-category' && <ForumsPage onNavigate={onNavigate} user={user} view="category" categorySlug={pageState.slug} />}
          {pageState.page === 'forum-thread' && <ForumsPage onNavigate={onNavigate} user={user} view="thread" threadId={pageState.id} />}
          {pageState.page === 'messages' && <MessagesPage onNavigate={onNavigate} user={user} initialConversationId={pageState.conversationId} initialUsername={pageState.username} />}
          {pageState.page === 'account-settings' && <AccountSettingsPage onNavigate={onNavigate} />}
          {pageState.page === 'forgot-password' && <ForgotPasswordPage onNavigate={onNavigate} />}
          {pageState.page === 'reset-password' && <ResetPasswordPage token={pageState.token} onNavigate={onNavigate} />}
          {pageState.page === 'blog' && <BlogListPage onNavigate={onNavigate} />}
          {pageState.page === 'blog-post' && <BlogPostPage slug={pageState.slug} onNavigate={onNavigate} updateTitle={(t: string) => { document.title = t; }} />}
          {(pageState.page === 'terms' || pageState.page === 'privacy' || pageState.page === 'content-guidelines' || pageState.page === 'community-guidelines') && (
            <LegalPage section={pageState.page as LegalSection} onNavigate={onNavigate} />
          )}
        </>
      )}

      {showNavbar && <SurpriseMe onNavigate={onNavigate} />}
      {showNavbar && <Footer onNavigate={onNavigate} />}
    </div>
    </ToastProvider>
  );
}
